import { CreateTransient, Icon } from "@mindlercare/ui-web";
import { differenceInDays, format, isPast, isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";

interface Props {
  feedbackDate?: string | null;
  hasFeedback: boolean;
  done: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const DateText = ({
  date,
  overdue,
  done,
}: {
  date: string | number | undefined | null;
  overdue: boolean;
  done: boolean;
}) => {
  const { t } = useTranslation();

  if (!date) {
    return (
      <Typography insensitive variant="body">
        {"--"}
      </Typography>
    );
  }

  const feedbackDate = new Date(date);
  const currentDate = new Date();
  const dueToday = isToday(feedbackDate);
  const daysPast = differenceInDays(currentDate, new Date(feedbackDate));

  return (
    <Typography variant="body" color={overdue ? "ERROR" : "GRAY_BLACK"}>
      {overdue
        ? t("OVERDUE", { days: daysPast })
        : dueToday
          ? t("DUE_TODAY")
          : t("DUE", {
              date: done
                ? format(feedbackDate, "MMM dd yyyy")
                : format(feedbackDate, "EEEE, MMM dd"),
            })}
    </Typography>
  );
};

export const FeedbackDateText = ({
  feedbackDate,
  hasFeedback,
  done,
}: Props) => {
  const date = new Date(feedbackDate ?? "");
  const overdue = feedbackDate
    ? isPast(date) && !isToday(date) && !hasFeedback
    : false;
  return (
    <Wrapper>
      <CalendarIcon $overdue={overdue} />
      <DateText date={feedbackDate} overdue={overdue} done={done} />
    </Wrapper>
  );
};

const CalendarIcon = styled(Icon.Calendar)<
  CreateTransient<{ overdue: boolean }>
>`
  color: ${({ theme, $overdue }) =>
    $overdue ? theme.COLORS.ERROR : theme.COLORS.GRAY};
`;
